import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { api } from "@/lib/api";
import { Loader2, Search, ShieldCheck, Snowflake, Users, Building2, User as UserIcon, Smartphone } from "lucide-react";

export default function AdminModal({ open, onClose }) {
  const { t, lang } = useApp();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (open) {
      setData(null); setQ("");
      api.get("/admin/customers").then((r) => setData(r.data)).catch(() => {});
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    if (!s) return data.customers;
    return data.customers.filter((c) =>
      (c.name || "").toLowerCase().includes(s) ||
      (c.handle || "").toLowerCase().includes(s) ||
      (c.email || "").toLowerCase().includes(s) ||
      (c.national_id || "").includes(s) ||
      (c.cr_number || "").includes(s));
  }, [data, q]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="admin-modal" className="max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> {t.adminCustomers}
          </DialogTitle>
          <DialogDescription>
            {data ? `${t.totalCustomers}: ${data.count}` : t.adminCustomers}
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="py-12 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground" />
              <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={t.searchCustomers} className="h-11 rounded-2xl ps-9" />
            </div>
            <div className="space-y-2" data-testid="admin-customer-list">
              {filtered.map((c) => (
                <div key={c.user_id} data-testid={`admin-customer-${c.handle}`} className="rounded-2xl border border-border bg-card p-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-8 h-8 rounded-xl grid place-items-center shrink-0 ${c.account_type === "business" ? "bg-indigo-500/15 text-indigo-500" : "bg-primary/10 text-primary"}`}>
                        {c.account_type === "business" ? <Building2 className="w-4 h-4" /> : <UserIcon className="w-4 h-4" />}
                      </span>
                      <div className="min-w-0">
                        <p className="font-bold text-sm truncate">{c.name} <span className="text-muted-foreground font-normal">@{c.handle}</span></p>
                        <p className="text-[11px] text-muted-foreground truncate">{c.email}</p>
                      </div>
                    </div>
                    <span className="font-black tabular-nums text-sm shrink-0">{formatSAR(c.balance, lang)} <span className="text-[10px] text-muted-foreground">{t.currency}</span></span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                    <Badge testid={`admin-verified-${c.handle}`} tone={c.verified ? "green" : "gray"} icon={ShieldCheck}>
                      {c.verified ? t.verifiedLabel : t.unverifiedLabel}
                    </Badge>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-muted text-muted-foreground">•••• {c.card_last4}</span>
                    {c.card_frozen && <Badge tone="blue" icon={Snowflake}>{t.frozenLabel}</Badge>}
                    {c.pin_set && <Badge tone="green">PIN</Badge>}
                    {(c.linked_wallets || []).map((w) => (
                      <Badge key={w} tone="violet" icon={Smartphone}>{w}</Badge>
                    ))}
                    {(c.national_id || c.cr_number) && (
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {c.account_type === "business" ? "CR" : "ID"} {c.national_id || c.cr_number}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const TONES = {
  green: "bg-emerald-500/15 text-emerald-600",
  gray: "bg-muted text-muted-foreground",
  blue: "bg-sky-500/15 text-sky-600",
  violet: "bg-violet-500/15 text-violet-600",
};
const Badge = ({ children, tone = "gray", icon: Icon, testid }) => (
  <span data-testid={testid} className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${TONES[tone]}`}>
    {Icon && <Icon className="w-3 h-3" />}{children}
  </span>
);
